/**
 * Ciclo viral de la App — Analytics → Producto.
 * RPC: get_product_viral_cycle_analytics(p_days)
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient } from '@/services/db-client';
import { SHARE_EVENT_LABELS } from '@/hooks/useProductGrowthLoop';

export interface ViralCycleLoopMetrics {
    active_users: number;
    active_users_previous: number;
    sharers: number;
    sharers_previous: number;
    sharer_penetration_pct: number | null;
    sharer_penetration_pct_previous: number | null;
    viral_actions: number;
    viral_actions_previous: number;
    actions_per_sharer: number | null;
    actions_per_active: number | null;
    short_urls_created: number;
    short_urls_created_previous: number;
    link_clicks: number;
    link_clicks_previous: number;
    click_through_pct: number | null;
    click_through_pct_previous: number | null;
    viral_arrivals: number;
    viral_arrivals_previous: number;
    attributed_signups: number;
    attributed_signups_previous: number;
    registrations: number;
    registrations_previous: number;
    invite_redemptions: number;
    inviters: number;
    k_factor: number | null;
}

export interface ViralFunnelRate {
    stage: string;
    label: string;
    rate_pct: number | null;
    numerator: number;
    denominator: number;
}

export interface ViralBreakdownRow {
    count: number;
    users?: number;
    method?: string;
    platform?: string;
    event_type?: string;
    share_type?: string;
    urls?: number;
    clicks?: number;
}

export interface ViralSharerBucket {
    bucket: string;
    sharers: number;
}

export interface ViralWeeklyPoint {
    week_start: string;
    shares: number;
    sharers: number;
    short_urls: number;
    clicks: number;
}

export interface ViralReferralQuality {
    invited_users: number;
    invited_activation_pct: number | null;
    invited_capture_pct: number | null;
    organic_users: number;
    organic_activation_pct: number | null;
    organic_capture_pct: number | null;
    invitation_codes_issued: number;
    inviters_all_time: number;
}

export interface ViralMeasurement {
    first_open_total: number;
    first_open_period: number;
    attribution_ready: boolean;
    short_urls_all_time: number;
    short_url_clicks_all_time: number;
}

export interface ViralDiagnosis {
    bottleneck_key: string;
    bottleneck_label: string;
    headline: string;
}

export interface ProductViralCycleAnalytics {
    period_days: number;
    comparison_label: string;
    loop: ViralCycleLoopMetrics;
    funnel_rates: ViralFunnelRate[];
    methods: ViralBreakdownRow[];
    platforms: ViralBreakdownRow[];
    content_types: ViralBreakdownRow[];
    short_url_types: ViralBreakdownRow[];
    sharer_distribution: ViralSharerBucket[];
    weekly_trend: ViralWeeklyPoint[];
    referral_quality: ViralReferralQuality;
    measurement: ViralMeasurement;
    diagnosis: ViralDiagnosis;
}

function asNumber(v: unknown, fallback = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
    }
    return fallback;
}

function asNullableNumber(v: unknown): number | null {
    if (v == null) return null;
    const n = asNumber(v, Number.NaN);
    return Number.isFinite(n) ? n : null;
}

function parseBreakdownRows(raw: unknown, key: 'method' | 'platform' | 'event_type' | 'share_type'): ViralBreakdownRow[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
        .map((row) => ({
            count: asNumber(row.count ?? row.urls),
            users: asNumber(row.users),
            urls: asNumber(row.urls),
            clicks: asNumber(row.clicks),
            method: key === 'method' ? String(row.method ?? '') : undefined,
            platform: key === 'platform' ? String(row.platform ?? '') : undefined,
            event_type: key === 'event_type' ? String(row.event_type ?? '') : undefined,
            share_type: key === 'share_type' ? String(row.share_type ?? '') : undefined,
        }));
}

function parseLoop(raw: unknown): ViralCycleLoopMetrics {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        active_users: asNumber(o.active_users),
        active_users_previous: asNumber(o.active_users_previous),
        sharers: asNumber(o.sharers),
        sharers_previous: asNumber(o.sharers_previous),
        sharer_penetration_pct: asNullableNumber(o.sharer_penetration_pct),
        sharer_penetration_pct_previous: asNullableNumber(o.sharer_penetration_pct_previous),
        viral_actions: asNumber(o.viral_actions),
        viral_actions_previous: asNumber(o.viral_actions_previous),
        actions_per_sharer: asNullableNumber(o.actions_per_sharer),
        actions_per_active: asNullableNumber(o.actions_per_active),
        short_urls_created: asNumber(o.short_urls_created),
        short_urls_created_previous: asNumber(o.short_urls_created_previous),
        link_clicks: asNumber(o.link_clicks),
        link_clicks_previous: asNumber(o.link_clicks_previous),
        click_through_pct: asNullableNumber(o.click_through_pct),
        click_through_pct_previous: asNullableNumber(o.click_through_pct_previous),
        viral_arrivals: asNumber(o.viral_arrivals),
        viral_arrivals_previous: asNumber(o.viral_arrivals_previous),
        attributed_signups: asNumber(o.attributed_signups),
        attributed_signups_previous: asNumber(o.attributed_signups_previous),
        registrations: asNumber(o.registrations),
        registrations_previous: asNumber(o.registrations_previous),
        invite_redemptions: asNumber(o.invite_redemptions),
        inviters: asNumber(o.inviters),
        k_factor: asNullableNumber(o.k_factor),
    };
}

export function parseProductViralCycleResponse(raw: unknown): ProductViralCycleAnalytics | null {
    if (raw == null) return null;
    let payload: unknown = raw;
    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (Array.isArray(payload)) payload = payload[0];
    if (!payload || typeof payload !== 'object') return null;
    const o = payload as Record<string, unknown>;
    const wrapped = o.get_product_viral_cycle_analytics;
    if (wrapped && typeof wrapped === 'object') payload = wrapped;
    const p = payload as Record<string, unknown>;
    if (typeof p.period_days !== 'number' && typeof p.period_days !== 'string') return null;

    const funnel = Array.isArray(p.funnel_rates)
        ? p.funnel_rates.map((row) => {
            const r = row as Record<string, unknown>;
            return {
                stage: typeof r.stage === 'string' ? r.stage : '',
                label: typeof r.label === 'string' ? r.label : '',
                rate_pct: asNullableNumber(r.rate_pct),
                numerator: asNumber(r.numerator),
                denominator: asNumber(r.denominator),
            };
        })
        : [];

    const sharerDist = Array.isArray(p.sharer_distribution)
        ? p.sharer_distribution.map((row) => {
            const r = row as Record<string, unknown>;
            return { bucket: String(r.bucket ?? ''), sharers: asNumber(r.sharers) };
        })
        : [];

    const weekly = Array.isArray(p.weekly_trend)
        ? p.weekly_trend.map((row) => {
            const r = row as Record<string, unknown>;
            return {
                week_start: String(r.week_start ?? ''),
                shares: asNumber(r.shares),
                sharers: asNumber(r.sharers),
                short_urls: asNumber(r.short_urls),
                clicks: asNumber(r.clicks),
            };
        })
        : [];

    const rq = (p.referral_quality && typeof p.referral_quality === 'object'
        ? p.referral_quality : {}) as Record<string, unknown>;
    const ms = (p.measurement && typeof p.measurement === 'object'
        ? p.measurement : {}) as Record<string, unknown>;
    const dg = (p.diagnosis && typeof p.diagnosis === 'object'
        ? p.diagnosis : {}) as Record<string, unknown>;

    return {
        period_days: asNumber(p.period_days, 30),
        comparison_label: typeof p.comparison_label === 'string' ? p.comparison_label : '',
        loop: parseLoop(p.loop),
        funnel_rates: funnel,
        methods: parseBreakdownRows(p.methods, 'method'),
        platforms: parseBreakdownRows(p.platforms, 'platform'),
        content_types: parseBreakdownRows(p.content_types, 'event_type'),
        short_url_types: parseBreakdownRows(p.short_url_types, 'share_type'),
        sharer_distribution: sharerDist,
        weekly_trend: weekly,
        referral_quality: {
            invited_users: asNumber(rq.invited_users),
            invited_activation_pct: asNullableNumber(rq.invited_activation_pct),
            invited_capture_pct: asNullableNumber(rq.invited_capture_pct),
            organic_users: asNumber(rq.organic_users),
            organic_activation_pct: asNullableNumber(rq.organic_activation_pct),
            organic_capture_pct: asNullableNumber(rq.organic_capture_pct),
            invitation_codes_issued: asNumber(rq.invitation_codes_issued),
            inviters_all_time: asNumber(rq.inviters_all_time),
        },
        measurement: {
            first_open_total: asNumber(ms.first_open_total),
            first_open_period: asNumber(ms.first_open_period),
            attribution_ready: ms.attribution_ready === true,
            short_urls_all_time: asNumber(ms.short_urls_all_time),
            short_url_clicks_all_time: asNumber(ms.short_url_clicks_all_time),
        },
        diagnosis: {
            bottleneck_key: typeof dg.bottleneck_key === 'string' ? dg.bottleneck_key : 'unknown',
            bottleneck_label: typeof dg.bottleneck_label === 'string' ? dg.bottleneck_label : '',
            headline: typeof dg.headline === 'string' ? dg.headline : '',
        },
    };
}

export function useProductViralCycle(days: number = 30) {
    return useQuery<ProductViralCycleAnalytics>({
        queryKey: ['admin', 'product-viral-cycle', days],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('get_product_viral_cycle_analytics', {
                p_days: days,
            });
            if (error) throw error;
            const parsed = parseProductViralCycleResponse(data);
            if (!parsed) {
                throw new Error('Respuesta inválida de get_product_viral_cycle_analytics');
            }
            return parsed;
        },
        retry: 1,
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export const VIRAL_LOOP_STAGES = [
    { key: 'active', label: 'Activos', field: 'active_users' as const },
    { key: 'sharers', label: 'Comparten', field: 'sharers' as const },
    { key: 'actions', label: 'Acciones virales', field: 'viral_actions' as const },
    { key: 'urls', label: 'URLs cortas', field: 'short_urls_created' as const },
    { key: 'clicks', label: 'Clicks en enlace', field: 'link_clicks' as const },
    { key: 'arrivals', label: 'Llegadas medidas', field: 'viral_arrivals' as const },
    { key: 'signups', label: 'Registros atribuibles', field: 'attributed_signups' as const },
] as const;

export const SHARE_METHOD_LABELS: Record<string, string> = {
    web_share_api: 'Share nativo',
    clipboard: 'Portapapeles',
    download: 'Descarga',
    contacts_whatsapp: 'WhatsApp contactos',
    unknown: 'Desconocido',
};

export const SHORT_URL_TYPE_LABELS: Record<string, string> = {
    catch: 'Capturas',
    user: 'Perfil',
    moment: 'Momentos',
    image: 'Imagen',
    profile: 'Perfil',
};

export function contentTypeLabel(eventType: string): string {
    return SHARE_EVENT_LABELS[eventType] ?? eventType.replace(/^share_/, '');
}
